import React, { FunctionComponent, useEffect, useContext, useRef } from 'react';
import { Player } from 'components/Player';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'rootReducer';
import { useAudio } from 'features/audio/audio';
import { useLocation } from 'react-router-dom';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useNativefullScreenChat } from 'features/chat/chatHooks';
import { useAppSelector } from 'hooks/redux';
import { useSubdomain } from 'hooks/useSubdomain';
import { fetchRecording } from 'features/recordings/recordingsSlice';
import { resetAudioState } from 'features/audio/audioSlice';
import { createAlert } from 'features/alert/alertSlice';

export const RecordingPlayer: FunctionComponent = () => {
  const { isNativeAppWebview, isListenerAppWebview, isEmbed } =
    useContext(UserAgentContext);
  const audioUrl = useSelector((state: RootState) => state.audio.url);
  const audioError = useAppSelector((state) => state.audio.error);
  const recordingId = useAppSelector(
    (state) => state.recordings.currentRecordingId
  );
  const { audio, loadAudio, togglePlayPause } = useAudio();
  const location = useLocation() as any;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const channelSubdomain = useSubdomain();
  const nativefullScreenChat = useNativefullScreenChat();
  const refreshAttemptedRef = useRef<boolean>(false);

  useEffect(() => {
    // If audio already set bail early
    if (audio || isListenerAppWebview) {
      return;
    }

    // Load the audio on render so recording
    // controls are active for user
    if (audioUrl) {
      loadAudio({
        audioSrc: audioUrl,
        autoPlay: (location.state && location.state.autoPlay) || false,
        audioType: 'recording'
      });
    }
  }, [audioUrl, loadAudio, audio, location, isListenerAppWebview]);

  useEffect(() => {
    if (!audioError || !recordingId) return;
    if (refreshAttemptedRef.current) {
      dispatch(
        createAlert({
          message: 'Error loading recording',
          type: 'error'
        })
      );
      return;
    }
    refreshAttemptedRef.current = true;
    console.log('[Recording] - Refreshing recording URL after audio error');
    dispatch(resetAudioState());
    dispatch(
      fetchRecording(channelSubdomain, recordingId, navigate, isEmbed, true)
    );
  }, [audioError, recordingId, dispatch, channelSubdomain, navigate, isEmbed]);

  useEffect(() => {
    refreshAttemptedRef.current = false;
  }, [recordingId]);

  const onClick = () => {
    togglePlayPause();
  };

  if (nativefullScreenChat) {
    return (
      <div className="tw-relative tw-w-full tw-text-center tw-h-[132px]"></div>
    );
  }

  if (isNativeAppWebview) {
    return null;
  }

  if (!audioUrl) {
    return null;
  }

  return (
    <div className="tw-relative tw-w-full tw-text-center tw-h-[100px]">
      <Player onClick={onClick} showsControls={true} type={'recording'} />
    </div>
  );
};
